import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const AIRLINE_COLORS = {
  AA: { bg: 'bg-red-600',    light: 'bg-red-50',    text: 'text-red-600',    label: 'American Airlines' },
  DL: { bg: 'bg-blue-700',   light: 'bg-blue-50',   text: 'text-blue-700',   label: 'Delta Air Lines' },
  UA: { bg: 'bg-blue-900',   light: 'bg-indigo-50', text: 'text-indigo-800', label: 'United Airlines' },
  WN: { bg: 'bg-emerald-500', light: 'bg-emerald-50', text: 'text-orange-600', label: 'Southwest Airlines' },
  B6: { bg: 'bg-cyan-600',   light: 'bg-cyan-50',   text: 'text-cyan-700',   label: 'JetBlue Airways' },
  AS: { bg: 'bg-teal-600',   light: 'bg-teal-50',   text: 'text-teal-700',   label: 'Alaska Airlines' },
  F9: { bg: 'bg-green-600',  light: 'bg-green-50',  text: 'text-green-700',  label: 'Frontier Airlines' },
  NK: { bg: 'bg-yellow-500', light: 'bg-yellow-50', text: 'text-yellow-700', label: 'Spirit Airlines' },
};

const DEFAULT_AIRLINE = { bg: 'bg-slate-600', light: 'bg-slate-50', text: 'text-slate-700', label: '' };

function AirlineLogo({ code }) {
  const style = AIRLINE_COLORS[code] || DEFAULT_AIRLINE;
  const [imgError, setImgError] = React.useState(false);

  if (!imgError) {
    return (
      <div className="w-11 h-11 rounded-xl bg-white border border-gray-100 shadow-sm flex items-center justify-center overflow-hidden shrink-0">
        <img
          src={`https://www.gstatic.com/flights/airline_logos/70px/${code}.png`}
          alt={code}
          className="w-9 h-9 object-contain"
          onError={() => setImgError(true)}
        />
      </div>
    );
  }

  return (
    <div className={`${style.bg} text-white font-extrabold text-xs w-11 h-11 rounded-xl flex items-center justify-center shadow-sm tracking-wide shrink-0`}>
      {code}
    </div>
  );
}

/* ── Single flight leg row ── */
function LegRow({ leg, label }) {
  const style = AIRLINE_COLORS[leg.airlineCode] || DEFAULT_AIRLINE;
  const airlineName = style.label || leg.airline || leg.airlineCode;

  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-3">
      {/* Label pill */}
      <span className={`text-xs font-bold px-2 py-0.5 rounded-full w-fit ${
        label === 'Outbound' ? 'bg-blue-100 text-blue-700' : 'bg-violet-100 text-violet-700'
      }`}>{label}</span>

      <div className="flex items-center gap-3 flex-1">
        {/* Airline */}
        <div className="flex items-center gap-2 min-w-[140px]">
          <AirlineLogo code={leg.airlineCode} />
          <div>
            <p className="font-bold text-gray-900 text-xs leading-tight">{airlineName}</p>
            <p className={`text-xs mt-0.5 ${style.text}`}>{leg.flightNumber}</p>
          </div>
        </div>

        {/* Timeline */}
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <div className="text-center min-w-[58px]">
            <p className="text-lg font-extrabold text-gray-900 leading-none">{leg.departureTime}</p>
            <p className="text-xs font-bold text-gray-600 mt-0.5">{leg.origin}</p>
          </div>

          <div className="flex-1 flex flex-col items-center min-w-0">
            <p className="text-xs text-gray-400 mb-1">{leg.duration}</p>
            <div className="w-full flex items-center">
              <div className="w-1.5 h-1.5 rounded-full bg-gray-300 shrink-0" />
              <div className="flex-1 h-px bg-gradient-to-r from-gray-300 via-blue-400 to-gray-300" />
              <svg className="w-4 h-4 text-blue-500 shrink-0" fill="currentColor" viewBox="0 0 24 24">
                <path d="M21 16v-2l-8-5V3.5A1.5 1.5 0 0 0 11.5 2 1.5 1.5 0 0 0 10 3.5V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5z"/>
              </svg>
              <div className="flex-1 h-px bg-gradient-to-r from-gray-300 via-blue-400 to-gray-300" />
              <div className="w-1.5 h-1.5 rounded-full bg-blue-500 shrink-0" />
            </div>
            <p className="text-xs mt-1 font-semibold">
              {leg.stops === 0
                ? <span className="text-emerald-600">Non-stop</span>
                : <span className="text-amber-500">{leg.stops} stop{leg.stops > 1 ? 's' : ''}</span>
              }
            </p>
          </div>

          <div className="text-center min-w-[58px]">
            <p className="text-lg font-extrabold text-gray-900 leading-none">{leg.arrivalTime}</p>
            <p className="text-xs font-bold text-gray-600 mt-0.5">{leg.destination}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function FlightCard({ flight, passengers, travelClass }) {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);

  const style = AIRLINE_COLORS[flight.airlineCode] || DEFAULT_AIRLINE;
  const airlineName = style.label || flight.airline || flight.airlineCode;
  const isRoundtrip = !!flight.returnFlight;

  const classLabel = {
    economy: 'Economy',
    business: 'Business',
    firstClass: 'First Class',
  }[travelClass] || 'Economy';

  const handleBook = () => navigate('/book', { state: { flight, passengers, travelClass } });

  return (
    <div className={`bg-white rounded-2xl border transition-all duration-200 overflow-hidden hover:shadow-xl hover:-translate-y-0.5 ${
      flight._isCheapest ? 'border-green-300 shadow-green-100 shadow-md' : 'border-gray-100 shadow-sm'
    }`}>
      {/* Top accent bar */}
      <div className={`h-1 ${style.bg} w-full`} />

      <div className="p-5">
        {/* Badges */}
        {(flight._isCheapest || flight._isFastest) && (
          <div className="flex gap-2 mb-3">
            {flight._isCheapest && <span className="bg-green-100 text-green-700 text-xs font-bold px-2.5 py-1 rounded-full">💰 Cheapest</span>}
            {flight._isFastest  && <span className="bg-blue-100 text-blue-700 text-xs font-bold px-2.5 py-1 rounded-full">⚡ Fastest</span>}
          </div>
        )}

        {/* ── ROUNDTRIP: two leg rows ── */}
        {isRoundtrip ? (
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1 flex flex-col gap-3">
              <LegRow leg={flight} label="Outbound" />
              <div className="border-t border-dashed border-gray-200" />
              <LegRow leg={flight.returnFlight} label="Return" />
            </div>

            {/* Divider */}
            <div className="hidden lg:block w-px bg-gray-100 mx-2" />

            {/* Price & CTA */}
            <div className="flex lg:flex-col items-center lg:items-end justify-between lg:min-w-[150px] gap-3 pt-2 lg:pt-0">
              <div className="text-left lg:text-right">
                <p className="text-xs text-gray-400">round trip · {parseInt(passengers) > 1 ? "avg. per person" : "per person"}</p>
                <p className={`text-3xl font-extrabold leading-none ${style.text}`}>
                  ${flight.price?.toLocaleString('en-US')}
                </p>
                {parseInt(passengers) > 1 && (
                  <p className="text-xs text-gray-500 mt-1">
                    Total: <span className="font-semibold text-gray-700">${flight.totalPrice?.toLocaleString('en-US')}</span>
                  </p>
                )}
              </div>
              <button
                onClick={handleBook}
                className={`${style.bg} hover:opacity-90 text-white font-bold px-6 py-2.5 rounded-xl text-sm transition-all shadow-sm hover:shadow-md active:scale-95 whitespace-nowrap`}
              >
                Select →
              </button>
            </div>
          </div>
        ) : (
          /* ── ONE-WAY: single row ── */
          <div className="flex flex-col lg:flex-row lg:items-center gap-5">
            {/* Airline */}
            <div className="flex items-center gap-3 min-w-[160px]">
              <AirlineLogo code={flight.airlineCode} />
              <div>
                <p className="font-bold text-gray-900 text-sm leading-tight">{airlineName}</p>
                <p className={`text-xs font-semibold mt-0.5 ${style.text}`}>{flight.flightNumber}</p>
                <p className="text-xs text-gray-400 mt-0.5">{classLabel}</p>
              </div>
            </div>

            {/* Timeline */}
            <div className="flex items-center gap-3 flex-grow">
              <div className="text-center min-w-[70px]">
                <p className="text-2xl font-extrabold text-gray-900 leading-none">{flight.departureTime}</p>
                <p className="text-sm font-bold text-gray-700 mt-1">{flight.origin}</p>
                <p className="text-xs text-gray-400 truncate max-w-[80px]">{flight.originCity}</p>
              </div>

              <div className="flex-grow flex flex-col items-center min-w-0">
                <p className="text-xs text-gray-500 mb-1 font-medium">{flight.duration}</p>
                <div className="relative w-full flex items-center">
                  <div className="w-2.5 h-2.5 rounded-full bg-gray-300 border-2 border-white ring-2 ring-gray-300 shrink-0" />
                  <div className="flex-grow h-px bg-gradient-to-r from-gray-300 via-blue-400 to-gray-300 mx-1" />
                  <svg className="w-5 h-5 text-blue-500 shrink-0" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M21 16v-2l-8-5V3.5A1.5 1.5 0 0 0 11.5 2 1.5 1.5 0 0 0 10 3.5V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5z"/>
                  </svg>
                  <div className="flex-grow h-px bg-gradient-to-r from-gray-300 via-blue-400 to-gray-300 mx-1" />
                  <div className="w-2.5 h-2.5 rounded-full bg-blue-600 border-2 border-white ring-2 ring-blue-400 shrink-0" />
                </div>
                <p className="text-xs mt-1 font-semibold">
                  {flight.stops === 0
                    ? <span className="text-emerald-600">Non-stop</span>
                    : <span className="text-amber-500">{flight.stops} Stop{flight.stops > 1 ? 's' : ''}</span>
                  }
                </p>
              </div>

              <div className="text-center min-w-[70px]">
                <p className="text-2xl font-extrabold text-gray-900 leading-none">{flight.arrivalTime}</p>
                <p className="text-sm font-bold text-gray-700 mt-1">{flight.destination}</p>
                <p className="text-xs text-gray-400 truncate max-w-[80px]">{flight.destinationCity}</p>
              </div>
            </div>

            <div className="hidden lg:block w-px h-14 bg-gray-100" />

            {/* Price & CTA */}
            <div className="flex lg:flex-col items-center lg:items-end justify-between lg:min-w-[150px] gap-3">
              <div className="text-left lg:text-right">
                <p className="text-xs text-gray-400">{parseInt(passengers) > 1 ? "avg. per person" : "per person"}</p>
                <p className={`text-3xl font-extrabold leading-none ${style.text}`}>
                  ${flight.price?.toLocaleString('en-US')}
                </p>
                {parseInt(passengers) > 1 && (
                  <p className="text-xs text-gray-500 mt-1">
                    Total: <span className="font-semibold text-gray-700">${flight.totalPrice?.toLocaleString('en-US')}</span>
                  </p>
                )}
              </div>
              <button
                onClick={handleBook}
                className={`${style.bg} hover:opacity-90 text-white font-bold px-6 py-2.5 rounded-xl text-sm transition-all shadow-sm hover:shadow-md active:scale-95 whitespace-nowrap`}
              >
                Select →
              </button>
            </div>
          </div>
        )}

        {/* Expand toggle */}
        <button
          onClick={() => setExpanded(!expanded)}
          className="mt-4 text-xs text-gray-400 hover:text-blue-600 flex items-center gap-1 transition-colors"
        >
          <span>{expanded ? 'Hide' : 'Show'} flight details</span>
          <svg className={`w-3 h-3 transition-transform ${expanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {/* Expanded details */}
        {expanded && (
          <div className="mt-3 pt-3 border-t border-gray-100 grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { label: 'Baggage',    value: flight.baggage || '1 carry-on' },
              { label: 'Class',      value: classLabel },
              { label: 'Seats left', value: `${flight.seats || '—'}` },
              { label: isRoundtrip ? 'Depart date' : 'Date', value: flight.date },
              ...(isRoundtrip && flight.returnFlight?.date
                ? [{ label: 'Return date', value: flight.returnFlight.date }]
                : []),
            ].map(item => (
              <div key={item.label} className={`${style.light} rounded-xl px-3 py-2`}>
                <p className="text-xs text-gray-500">{item.label}</p>
                <p className={`text-sm font-bold ${style.text}`}>{item.value}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
